import React, { useMemo } from 'react';
import { compose } from 'redux';
import {
	RefreshControl,
	ScrollView,
	View,
	useWindowDimensions,
} from 'react-native';

import { POS_TYPE, ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils';
import { calculatePlanogramItemsPositions, comparePlanogramItemGroupsForDisplay } from '../../../common/utils/planograms/planogram';
import { getProductHealthComplianceLocalLabel } from '../../../common/utils/products/healthCompliance';
import { sortPlanogramItems } from '../../../common/utils/vending-utils';
import { getFullMachineName } from '../../../common/utils/machine';
import { compareItems } from '../../../common/utils/utils';
import { DetailContainer } from '../../containers';
import { Text } from '../../components';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { EmptyState, SecondaryButton, SectionTitle, SegmentedControl } from '../../design/components';
import PageHeading from '../../design/components/PageHeading';
import PointOfSaleMarketItemDialog from './PointOfSaleMarketItemDialog';
import PointOfSalePlanogramGrid from './PointOfSalePlanogramGrid';
import { MARKET_VIEW_MODES, VIEW_MODES } from './PointOfSalePlanogramHelpers';
import PointOfSalePlanogramListItem from './PointOfSalePlanogramListItem';
import usePointOfSaleMarketItemEditor from './usePointOfSaleMarketItemEditor';
import usePointOfSalePlanogramItemActions from './usePointOfSalePlanogramItemActions';
import usePointOfSalePlanogramViewModes from './usePointOfSalePlanogramViewModes';

const PointOfSalePlanogram = ({
	data: pointOfSale,
	pointOfSaleName,
	refetch,
}) => {
	const { tenant, tenantSettings, hasPermission, isDriver, isAVC } = useUserContext();
	const styles = useThemedStyles(createStyles);
	const { width } = useWindowDimensions();
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const {
		viewMode,
		marketViewMode,
		handleSetMachineViewMode,
		handleSetMarketViewMode,
	} = usePointOfSalePlanogramViewModes(width);

	const isMarket = pointOfSale?.type === POS_TYPE.MARKET;
	const canModifyPlanograms = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PLANOGRAMS);
	const canModifyMarketItems = canModifyPlanograms && isMarket;
	const planogramEditDisabled = isDriver && isAVC;
	const isCapacityEnabled = Boolean(tenantSettings?.enablePlanogramItemCapacity);
	const planogramId = pointOfSale?.planogram?.id;
	const {
		actionItemId,
		isMutatingPlanogramItems,
		createItemAfter,
		createFirstItem,
		moveItemByOffset,
		confirmRemoveItem,
	} = usePointOfSalePlanogramItemActions({ planogramId, canModifyMarketItems, refetch });

	const sortedItems = useMemo(() => {
		const items = pointOfSale?.planogram?.items || [];
		const sortedPlanogramItems = sortPlanogramItems(items, false) || [];
		return sortedPlanogramItems.filter(item => item.enabled !== false);
	}, [pointOfSale?.planogram?.items]);

	const { openMarketItemDialog, ...marketItemDialogProps } = usePointOfSaleMarketItemEditor({
		pointOfSale,
		sortedItems,
		canModifyMarketItems,
		planogramEditDisabled,
		isCapacityEnabled,
		refetch,
	});
	const isMutatingPlanogram = isMutatingPlanogramItems || marketItemDialogProps.isSavingMarketItemDialog;

	const itemsWithCoordinates = useMemo(() => {
		return sortedItems.filter(item => Number.isFinite(item?.row) && Number.isFinite(item?.column));
	}, [sortedItems]);

	const positionedSortedItems = useMemo(() => {
		if (!sortedItems.length) {
			return [];
		}
		return calculatePlanogramItemsPositions({
			items: sortedItems,
			planogramItemGroups: pointOfSale?.planogram?.planogramItemGroups,
		});
	}, [sortedItems, pointOfSale?.planogram?.planogramItemGroups]);

	const hasGridCoordinates = itemsWithCoordinates.length > 0;

	const rowsWithItems = useMemo(() => {
		if (!hasGridCoordinates) {
			return [];
		}

		const rowsMap = itemsWithCoordinates.reduce((acc, item) => {
			if (!acc[item.row]) {
				acc[item.row] = [];
			}
			acc[item.row].push(item);
			return acc;
		}, {});

		return Object.keys(rowsMap)
			.map(rowKey => Number(rowKey))
			.sort((a, b) => a - b)
			.map(row => ({
				row,
				items: rowsMap[row].sort((itemA, itemB) => itemA.column - itemB.column),
			}));
	}, [hasGridCoordinates, itemsWithCoordinates]);

	const marketProductItems = useMemo(() => {
		if (!isMarket) {
			return [];
		}

		const itemsByProductId = {};
		for (const item of positionedSortedItems) {
			if (!item.product?.id) {
				// eslint-disable-next-line no-continue
				continue;
			}

			if (!itemsByProductId[item.product.id]) {
				itemsByProductId[item.product.id] = {
					id: `market-product-${item.product.id}`,
					product: item.product,
					price: item.price,
					quantity: 0,
					parlevel: 0,
					capacity: 0,
					position: 0,
				};
			}

			itemsByProductId[item.product.id].quantity += (item.quantity || 0);
			itemsByProductId[item.product.id].parlevel += (item.parlevel || 0);
			itemsByProductId[item.product.id].capacity += (item.capacity || 0);
			itemsByProductId[item.product.id].position += (item.position || 0);
		}

		return Object.values(itemsByProductId).sort(compareItems('position', 1));
	}, [isMarket, positionedSortedItems]);

	const marketItemsByGroup = useMemo(() => {
		if (!isMarket) {
			return [];
		}

		const planogramItemGroups = pointOfSale?.planogram?.planogramItemGroups || [];
		const sortedPlanogramItemGroups = [...planogramItemGroups].sort(comparePlanogramItemGroupsForDisplay);
		const groupedPlanogramItems = sortedPlanogramItemGroups.map(planogramItemGroup => {
			const groupItems = positionedSortedItems
				.filter(item => item.planogramItemGroupId === planogramItemGroup.id)
				.sort(compareItems('position', 1));

			return {
				...planogramItemGroup,
				items: groupItems,
				productOptions: groupItems,
			};
		});

		const groupedItemIds = new Set(sortedPlanogramItemGroups.map(group => group.id));
		const ungroupedItems = positionedSortedItems.filter(item => (
			!item.planogramItemGroupId || !groupedItemIds.has(item.planogramItemGroupId)
		));
		if (ungroupedItems.length) {
			groupedPlanogramItems.push({
				id: 'ungrouped-planogram-items',
				name: 'Ungrouped',
				items: ungroupedItems,
				productOptions: ungroupedItems,
			});
		}

		return groupedPlanogramItems;
	}, [isMarket, pointOfSale?.planogram?.planogramItemGroups, positionedSortedItems]);

	const pointOfSaleDisplayName = useMemo(() => {
		if (pointOfSaleName) {
			return pointOfSaleName;
		}
		return getFullMachineName(pointOfSale, pointOfSale?.place, pointOfSale?.location);
	}, [pointOfSale, pointOfSaleName]);
	const healthComplianceLabel = useMemo(() => {
		return tenant ? getProductHealthComplianceLocalLabel(tenant, true) : 'HCG';
	}, [tenant]);

	// Shared by every list row; per-row options (hideQuantity, itemList, ...) are passed alongside.
	const listItemProps = {
		tenant,
		healthComplianceLabel,
		isMarket,
		canModifyMarketItems,
		isMutatingPlanogram,
		actionItemId,
		openMarketItemDialog,
		createItemAfter,
		moveItemByOffset,
		confirmRemoveItem,
	};

	const showGrid = viewMode === VIEW_MODES.GRID && hasGridCoordinates;
	const isEmptyPlanogram = sortedItems.length === 0;

	return (
		<>
			<ScrollView
				style={styles.scrollView}
				contentContainerStyle={styles.contentContainer}
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			>
				<PageHeading
					title={pointOfSaleDisplayName}
					subtitle={`Planogram selections: ${sortedItems.length}`}
					style={styles.headingContainer}
				/>

				{
					isEmptyPlanogram ? (
						<View style={styles.emptyState}>
							<EmptyState
								icon="grid"
								title="No planogram selections"
								message="No planogram selections are available for this point of sale."
							/>
							{
								canModifyMarketItems && (
									<SecondaryButton
										label="Add first item"
										icon="plus"
										style={styles.emptyStateButton}
										disabled={isMutatingPlanogram}
										onPress={() => {
											void createFirstItem({});
										}}
									/>
								)
							}
						</View>
					) : (
						<>
							<View style={styles.modeSwitcherContainer}>
								{
									isMarket ? (
										<SegmentedControl
											options={[
												{ value: MARKET_VIEW_MODES.PRODUCTS, label: 'Products' },
												{ value: MARKET_VIEW_MODES.ASSETS, label: 'Assets' },
											]}
											value={marketViewMode}
											onChange={handleSetMarketViewMode}
										/>
									) : (
										<SegmentedControl
											options={[
												{ value: VIEW_MODES.GRID, label: 'Grid' },
												{ value: VIEW_MODES.LIST, label: 'List' },
											]}
											value={viewMode}
											onChange={handleSetMachineViewMode}
										/>
									)
								}
								{
									(isMarket || viewMode === VIEW_MODES.GRID) && (
										<Text style={styles.modeHint}>
											{
												isMarket
													? 'Choose product list or asset-grouped list.'
													: 'Grid view scrolls horizontally on smaller phones.'
											}
										</Text>
									)
								}
							</View>

							{
								isMarket ? (
									marketViewMode === MARKET_VIEW_MODES.ASSETS ? (
										<View style={styles.groupedListView}>
											{
												marketItemsByGroup.map(planogramItemGroup => (
													<View key={planogramItemGroup.id} style={styles.groupSection}>
														<SectionTitle
															title={planogramItemGroup.name || 'Unnamed asset'}
															meta={`${planogramItemGroup.productOptions?.length || 0} options`}
															style={styles.groupSectionHeader}
														/>
														{
															planogramItemGroup.productOptions?.length ? (
																planogramItemGroup.productOptions.map((item, itemIndex) => (
																	<PointOfSalePlanogramListItem
																		key={item.id}
																		item={item}
																		hideQuantity={true}
																		itemList={planogramItemGroup.productOptions}
																		itemIndex={itemIndex}
																		{...listItemProps}
																	/>
																))
															) : (
																<>
																	<Text style={styles.groupSectionEmpty}>No products</Text>
																	{
																		canModifyMarketItems && (
																			<SecondaryButton
																				label="Add first item"
																				icon="plus"
																				style={styles.groupSectionAction}
																				disabled={isMutatingPlanogram}
																				onPress={() => {
																					void createFirstItem({
																						planogramItemGroupId: planogramItemGroup.id === 'ungrouped-planogram-items'
																							? undefined
																							: planogramItemGroup.id,
																					});
																				}}
																			/>
																		)
																	}
																</>
															)
														}
													</View>
												))
											}
										</View>
									) : (
										<View style={styles.listView}>
											{
												marketProductItems.map(item => (
													<PointOfSalePlanogramListItem
														key={item.id}
														item={item}
														allowActions={false}
														allowEditDialog={false}
														{...listItemProps}
													/>
												))
											}
										</View>
									)
								) : showGrid ? (
									<PointOfSalePlanogramGrid
										rowsWithItems={rowsWithItems}
										width={width}
										tenant={tenant}
										healthComplianceLabel={healthComplianceLabel}
									/>
								) : (
									<View style={styles.listView}>
										{
											sortedItems.map((item, itemIndex) => (
												<PointOfSalePlanogramListItem
													key={item.id}
													item={item}
													itemList={sortedItems}
													itemIndex={itemIndex}
													{...listItemProps}
												/>
											))
										}
									</View>
								)
							}
						</>
					)
				}
			</ScrollView>
			<PointOfSaleMarketItemDialog
				pointOfSale={pointOfSale}
				planogramEditDisabled={planogramEditDisabled}
				isCapacityEnabled={isCapacityEnabled}
				{...marketItemDialogProps}
			/>
		</>
	);
};

const createStyles = ({ ui: { colors, type, space, layout } }) => ({
	scrollView: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	contentContainer: {
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
		paddingTop: 20,
		paddingBottom: space.xl,
		gap: space.lg,
	},
	headingContainer: {
		paddingHorizontal: space.gutter,
	},
	emptyState: {
		paddingHorizontal: space.gutter,
		alignItems: 'center',
	},
	emptyStateButton: {
		minWidth: 160,
	},
	modeSwitcherContainer: {
		paddingHorizontal: space.gutter,
		gap: space.sm,
	},
	modeHint: {
		...type.caption,
		color: colors.textSecondary,
	},
	listView: {
		paddingHorizontal: space.gutter,
		gap: space.sm,
	},
	groupedListView: {
		paddingHorizontal: space.gutter,
		gap: space.lg,
	},
	groupSection: {
		gap: space.sm,
	},
	groupSectionHeader: {
		paddingHorizontal: 2,
	},
	groupSectionEmpty: {
		...type.secondary,
		color: colors.textSecondary,
		paddingHorizontal: 2,
	},
	groupSectionAction: {
		alignSelf: 'flex-start',
	},
});

export default compose(DetailContainer)(PointOfSalePlanogram);
